import { runAskUserQuestion } from '../../orchestrator/flows/askUserQuestion';
import { handleBotResponse } from '../../orchestrator/flows/botResponse';

/**
 * @typedef {Object} JourneyContentDescriptor
 * @property {() => Promise<object>} load  a dynamic-import loader for the journey's body
 *   component. It is a loader, not the component, so a module that imports the manifest does
 *   not pull in the heavy content. The Shell loads it lazily on open (see journeys/JourneyContent)
 *   and owns the loading placeholder.
 * @property {string} exportName  the named export to pull off the resolved module
 * @property {string} [label]     human label for the lazy boundary
 *
 * @typedef {Object} Journey
 * @property {string} id
 * @property {Object<string, (message: object) => import('react').ReactNode[]>} [extraMessageRenderers]
 *   the message types this journey ADDS over the base registry map (a colliding key
 *   overrides the base renderer). Never re-state the base map (see the renderer-free note
 *   below). Omit it when the journey adds no net-new types.
 * @property {Object} flows       the conversational flows this journey runs
 * @property {Object} config      journey-specific config/flags, including which optional
 *   domain BLOCKS the journey opts into (today: `liveAgent`, `queryLayer`). The orchestrator reads this
 *   the same way it reads `flows` — injected from above by AssistantDataProvider, since it
 *   is mounted ABOVE JourneyProvider and cannot call useJourney().
 * @property {JourneyContentDescriptor} content  where the journey's body component loads from
 * @property {React.ComponentType<{onClose: Function, isMobile?: boolean}>} [header]  a
 *   journey's OWN header component, for a journey whose content does not expect the shared
 *   orchestrator-coupled default markup (today: `legacy`, see AssistantShell.jsx, which
 *   reads this field and renders the default itself when it is absent). Omit it to use the
 *   default. A direct reference, not a loader: see .claude/context/shell-composition.md.
 * @property {boolean} [bareContent]  true if this journey's content brings its own composer
 *   and message area and must NOT be nested inside the shared DrawerContent frame (today:
 *   `legacy`, whose package renders its own composer — nesting it would produce two). See
 *   AssistantShell.jsx, which reads this field.
 */

/**
 * The default journey manifest. MUST STAY RENDERER-FREE: `legacy` and `paintColorPicker` import
 * it statically. See .claude/context/journey-resolution.md.
 *
 * @type {Journey}
 */
export const AssistantJourney = {
  id: 'assistant',
  flows: { runAskUserQuestion, handleBotResponse },
  // OPT-IN (`=== true`): omitting a block here means the journey runs without it.
  config: { liveAgent: true, queryLayer: true },
  content: {
    load: () => import('./MagicApronAssistant'),
    exportName: 'MagicApronAssistant',
    label: 'Assistant',
  },
};

// Default export required by useResolvedJourney's mod.default read — see
// .claude/context/journey-resolution.md for what breaks silently without it.
export default AssistantJourney;
